import { type ChangeEvent, useRef, useState } from "react";
import { CloseIcon, SearchIcon } from "~/icons";
import { Box } from "../Box/Box";

export function CategorySearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}): React.ReactNode {
  const [expanded, setExpanded] = useState(Boolean(value));
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearchIconClick = () => {
    inputRef.current?.focus();
    setExpanded(true);
  };

  return (
    <>
      <Box
        component="button"
        aria-label="Open search"
        height="touchable"
        width="touchable"
        alignItems="center"
        justifyContent="center"
        display="flex"
        border="standard"
        borderColor="neutral-300"
        borderRadius="standard"
        background="white"
        onClick={handleSearchIconClick}
        role="button"
        type="button"
      >
        <SearchIcon />
      </Box>
      <Box
        component="form"
        display="flex"
        background="neutral-100"
        position="absolute"
        style={{
          outline: "none",
          right: 0,
          transition: "all linear 0.2s",
          ...(expanded
            ? { border: "1px solid var(--color-neutral-300)", width: "100%" }
            : { border: "0px solid var(--color-neutral-300)", width: 0 }),
        }}
        height="touchable"
        borderRadius="standard"
        onReset={() => {
          onChange("");
          setExpanded(false);
        }}
        onSubmit={(e) => {
          e.preventDefault();
        }}
        role="search"
      >
        <Box
          component="input"
          ref={inputRef}
          id="menuSearch"
          placeholder="Search"
          height="full"
          aria-label="Search menu items"
          style={{
            outline: "none",
            transition: "all linear 0.2s",
            ...(expanded ? { flex: 1, padding: "10px" } : { width: 0 }),
          }}
          borderRadius="standard"
          value={value}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            onChange(event.currentTarget.value);
          }}
          onFocus={() => {
            setExpanded(true);
          }}
          onBlur={() => {
            // set to boolean(value) to ensure the search doesn't close if there is a search value defined
            setExpanded(Boolean(value));
          }}
          tabIndex={expanded ? 0 : -1}
        />
        {value && (
          <Box
            component="button"
            background="neutral-100"
            borderRadius="standard"
            type="reset"
            height="full"
            width="touchable"
            display="flex"
            alignItems="center"
            justifyContent="center"
            aria-label="Clear search"
            tabIndex={expanded ? 0 : -1}
          >
            <CloseIcon />
          </Box>
        )}
      </Box>
    </>
  );
}
